import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  AiOutlineHeart,
  AiFillHeart,
  AiOutlineShoppingCart,
  AiOutlineArrowRight,
} from "react-icons/ai";
import { MdOutlineBarChart } from "react-icons/md";
import { FaStar, FaRegStar, FaStarHalfAlt } from "react-icons/fa";
import ProductSkeleton from "./ProductSkeleton";
import axios from "axios";
import { toast } from "react-toastify";

const ProductDisplay = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingToCart, setAddingToCart] = useState({});
  const [wishlist, setWishlist] = useState([]);
  const [comparison, setComparison] = useState([]);
  const [processingWishlist, setProcessingWishlist] = useState({});
  const [processingComparison, setProcessingComparison] = useState({});
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const getUserData = async () => {
      // Get user data from localStorage
      const storedUserData = localStorage.getItem("BricksNbar-Users-Data");
      if (storedUserData) {
        const parsedUserData = JSON.parse(storedUserData);
        setUserData(parsedUserData);
        fetchWishlist(parsedUserData._id);
        fetchComparison(parsedUserData._id);
      }
    };

    getUserData();
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/products/get-all-products`
      );
      const data = await response.json();
      setProducts(data);
      setLoading(false);
    } catch (err) {
      setError("Failed to load products");
      setLoading(false);
    }
  };

  const fetchWishlist = async (userId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/wishlist/wishlist-by-user/${userId}`
      );

      if (response.data && response.data.products) {
        const wishlistProductIds = response.data.products.map(
          (product) => product._id
        );
        setWishlist(wishlistProductIds);
      }
    } catch (error) {
      console.error("Error fetching wishlist:", error);
    }
  };

  const fetchComparison = async (userId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/comparison/comparison-by-user/${userId}`
      );

      if (response.data && response.data.products) {
        const comparisonProductIds = response.data.products.map(
          (product) => product._id
        );
        setComparison(comparisonProductIds);
      }
    } catch (error) {
      console.error("Error fetching comparison list:", error);
    }
  };

  const displayedProducts = products.slice(0, 8);

  const handleViewAll = () => {
    navigate("/products");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderRatingStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating - fullStars >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={`full-${i}`} className="h-4 w-4" />);
    }

    if (hasHalfStar) {
      stars.push(<FaStarHalfAlt key="half" className="h-4 w-4" />);
    }

    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<FaRegStar key={`empty-${i}`} className="h-4 w-4" />);
    }

    return stars;
  };

  const handleNavigateToProductDetails = (ProductId) => {
    navigate(`/product-details/${ProductId}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Wishlist functions
  const isInWishlist = (productId) => {
    return wishlist.includes(productId);
  };

  const handleAddToWishlist = async (event, productId) => {
    event.stopPropagation();

    if (!userData) {
      navigate("/sign-in");
      return;
    }

    try {
      setProcessingWishlist({ ...processingWishlist, [productId]: true });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/wishlist/add-to-wishlist`,
        {
          userId: userData._id,
          productId: productId,
        }
      );

      if (response.data) {
        setWishlist([...wishlist, productId]);
        // toast.success("Product added to wishlist!");
      }
    } catch (error) {
      console.error("Error adding to wishlist:", error);
      toast.error("Failed to add to wishlist");
    } finally {
      setProcessingWishlist({ ...processingWishlist, [productId]: false });
    }
  };

  const handleRemoveFromWishlist = async (event, productId) => {
    event.stopPropagation();

    if (!userData) {
      navigate("/sign-in");
      return;
    }

    try {
      setProcessingWishlist({ ...processingWishlist, [productId]: true });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/wishlist/remove-from-wishlist`,
        {
          userId: userData._id,
          productId: productId,
        }
      );

      if (response.data) {
        setWishlist(wishlist.filter((id) => id !== productId));
        // toast.success("Product removed from wishlist!");
      }
    } catch (error) {
      console.error("Error removing from wishlist:", error);
      toast.error("Failed to remove from wishlist");
    } finally {
      setProcessingWishlist({ ...processingWishlist, [productId]: false });
    }
  };

  const handleToggleWishlist = (event, productId) => {
    if (isInWishlist(productId)) {
      handleRemoveFromWishlist(event, productId);
    } else {
      handleAddToWishlist(event, productId);
    }
  };

  // Comparison functions
  const isInComparison = (productId) => {
    return comparison.includes(productId);
  };

  const handleAddToComparison = async (event, productId) => {
    event.stopPropagation();

    if (!userData) {
      navigate("/sign-in");
      return;
    }

    try {
      setProcessingComparison({ ...processingComparison, [productId]: true });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/comparison/add-to-comparison`,
        {
          userId: userData._id,
          productId: productId,
        }
      );

      if (response.data) {
        setComparison([...comparison, productId]);
        toast.success("Product added to comparison!");
      }
    } catch (error) {
      console.error("Error adding to comparison:", error);
      toast.error("Failed to add to comparison");
    } finally {
      setProcessingComparison({ ...processingComparison, [productId]: false });
    }
  };

  const handleRemoveFromComparison = async (event, productId) => {
    event.stopPropagation();

    if (!userData) {
      navigate("/sign-in");
      return;
    }

    try {
      setProcessingComparison({ ...processingComparison, [productId]: true });

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/comparison/remove-from-comparison`,
        {
          userId: userData._id,
          productId: productId,
        }
      );

      if (response.data) {
        setComparison(comparison.filter((id) => id !== productId));
        toast.success("Product removed from comparison!");
      }
    } catch (error) {
      console.error("Error removing from comparison:", error);
      toast.error("Failed to remove from comparison");
    } finally {
      setProcessingComparison({ ...processingComparison, [productId]: false });
    }
  };

  const handleToggleComparison = (event, productId) => {
    if (isInComparison(productId)) {
      handleRemoveFromComparison(event, productId);
    } else {
      handleAddToComparison(event, productId);
    }
  };

  const handleAddToCart = async (event, productId) => {
    event.stopPropagation();

    try {
      setAddingToCart({ ...addingToCart, [productId]: true });

      const userData = localStorage.getItem("BricksNbar-Users-Data");
      const userId = userData ? JSON.parse(userData)._id : null;

      if (!userId) {
        navigate("/sign-in");
        return;
      }

      const config = {
        method: "post",
        url: `${process.env.REACT_APP_API_URL}/cart/add-product-to-cart`,
        headers: {
          "Content-Type": "application/json",
        },
        data: {
          userId: userId,
          productId: productId,
          quantity: 1,
        },
      };

      const response = await axios.request(config);

      console.log("Product added to cart:", response.data);
      toast.success("Product added to cart!");
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add product to cart");
    } finally {
      setAddingToCart({ ...addingToCart, [productId]: false });
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="flex sm:justify-between sm:items-center items-end mb-6 justify-end flex-col sm:flex-row">
        <h2 className="lg:text-3xl font-bold text-gray-800 text-2xl sm:text-2xl">
          Here's Something Worth Checking Out
        </h2>
        <p
          className="text-red-600 font-medium flex items-center cursor-pointer"
          onClick={(e) => {
            handleViewAll();
          }}
        >
          View All
          <AiOutlineArrowRight className="ml-1" />
        </p>
      </div>

      {loading ? (
        <ProductSkeleton />
      ) : error ? (
        <div className="text-red-500 text-center">{error}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {displayedProducts.map((product) => (
            <div
              key={product._id}
              className="border border-gray-200 rounded-md overflow-hidden shadow-sm hover:shadow-md transition-shadow hover:border-orange-700"
              onClick={() => {
                handleNavigateToProductDetails(product._id);
              }}
            >
              <div className="relative">
                {product.stock_status === "out of stock" && (
                  <span className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                    Out Of Stock
                  </span>
                )}

                <div className="h-48 flex items-center justify-center bg-gray-100">
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="absolute top-2 right-2 flex flex-col gap-2">
                  <button
                    className="p-1 rounded-full bg-white shadow hover:bg-gray-100"
                    onClick={(e) => handleToggleWishlist(e, product._id)}
                    disabled={processingWishlist[product._id]}
                  >
                    {processingWishlist[product._id] ? (
                      <span className="block h-5 w-5 border-t-2 border-r-2 border-gray-700 rounded-full animate-spin"></span>
                    ) : isInWishlist(product._id) ? (
                      <AiFillHeart className="text-red-500" size={20} />
                    ) : (
                      <AiOutlineHeart className="text-gray-500" size={20} />
                    )}
                  </button>
                  <button
                    className="p-1 rounded-full bg-white shadow hover:bg-gray-100"
                    onClick={(e) => handleToggleComparison(e, product._id)}
                    disabled={processingComparison[product._id]}
                  >
                    {processingComparison[product._id] ? (
                      <span className="block h-5 w-5 border-t-2 border-r-2 border-gray-700 rounded-full animate-spin"></span>
                    ) : isInComparison(product._id) ? (
                      <MdOutlineBarChart
                        className="text-yellow-500"
                        size={20}
                      />
                    ) : (
                      <MdOutlineBarChart className="text-gray-500" size={20} />
                    )}
                  </button>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-sm text-gray-500 capitalize">
                  {product.category?.name || "Category"}
                </h3>
                <h2 className="font-medium text-gray-800 capitalize">
                  {product.name}
                </h2>

                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-bold text-gray-800">
                      ₹ {product.price}
                    </span>
                    {product.previous_price && (
                      <span className="text-gray-500 line-through text-sm ml-2">
                        ₹ {product.previous_price}
                      </span>
                    )}
                  </div>
                  {product.stock_status !== "out of stock" && (
                    <button
                      className="p-2 rounded-full bg-gray-200 hover:bg-gray-300 relative"
                      onClick={(e) => handleAddToCart(e, product._id)}
                      disabled={addingToCart[product._id]}
                    >
                      {addingToCart[product._id] ? (
                        <span className="block h-5 w-5 border-t-2 border-r-2 border-gray-700 rounded-full animate-spin"></span>
                      ) : (
                        <AiOutlineShoppingCart className="h-5 w-5 text-gray-700" />
                      )}
                    </button>
                  )}
                </div>
                <div className="flex items-center text-yellow-400">
                  {renderRatingStars(product.ratings || 0)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductDisplay;
